import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth, isAdmin, isClient, isAppraiser } from "../context/AuthContext";
import { Buildings, List, X, SignOut, User as UserIcon } from "@phosphor-icons/react";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "./ui/dropdown-menu";

const linkCls = ({ isActive }) =>
  `px-3 py-2 rounded-full text-sm font-medium transition-colors ${
    isActive ? "bg-[#115E59] text-white" : "text-zinc-700 hover:bg-zinc-100"
  }`;

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const links = [];
  if (user) {
    links.push({ to: "/dashboard", label: "Dashboard" });
    if (isClient(user)) {
      links.push({ to: "/post-job", label: "Post job" });
      links.push({ to: "/my-jobs", label: "My jobs" });
    }
    if (isAppraiser(user)) {
      links.push({ to: "/open-jobs", label: "Open jobs" });
      links.push({ to: "/my-bids", label: "My bids" });
      links.push({ to: "/my-work", label: "My work" });
      if (user.is_reviewer) links.push({ to: "/review-pool", label: "Review pool" });
    }
    if (isAdmin(user)) {
      links.push({ to: "/admin", label: "Admin" });
    }
  }

  return (
    <header className="glass-header sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
        <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2" data-testid="nav-brand-link">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[#115E59] text-white">
            <Buildings weight="duotone" size={20} />
          </span>
          <span className="display-serif text-xl">Ledger &amp; Lot</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1" data-testid="nav-desktop">
          {links.map(l => (
            <NavLink key={l.to} to={l.to} className={linkCls} data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g,'-')}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="nav-user-menu" className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1 hover:bg-zinc-100 transition-colors">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user.picture} />
                    <AvatarFallback>{user.name?.[0]?.toUpperCase() || "U"}</AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:inline text-sm font-medium">{user.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="nav-profile-item">
                  <UserIcon size={16} className="mr-2" /> Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={logout} data-testid="nav-logout-item">
                  <SignOut size={16} className="mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/" className="btn-ink" data-testid="nav-signin-link">Sign in</Link>
          )}
          <button
            className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-full bg-zinc-100"
            onClick={() => setOpen(!open)}
            data-testid="nav-hamburger"
            aria-label="Toggle menu"
          >
            {open ? <X size={20} /> : <List size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-black/5 bg-white" data-testid="nav-mobile-panel">
          <nav className="max-w-7xl mx-auto px-5 py-3 flex flex-col gap-1">
            {links.map(l => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) => `px-4 py-3 rounded-xl text-base ${isActive ? "bg-[#115E59] text-white" : "text-zinc-800 hover:bg-zinc-50"}`}
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g,'-')}`}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
